import type {
  BdCondition} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  FilterConditionPluginSpecification} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterDetailedText } from '../filter-detailed-text';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const AYS_CLINIC_YEAR_CONDITION_ID = 'year';

/**
 * BDA-6020 — the MEMBERSHIP year of a clinic pool.
 *
 * ## The two years, and why this is not the other one
 *
 * `year` carries `supportedTargets: ['CLINIC','SCOPE']` in
 * `GET /clinics/available-conditions`, which is the API saying it means two
 * different things depending on where it sits:
 *
 * - **CLINIC grain — this plugin.** A node in `benchmark.clinicCondition`,
 *   i.e. in the Klinik-Cube's "Datenbasis Benchmarking". It decides WHICH
 *   clinics are in the pool: "clinics that had under 300 beds in 2022".
 * - **SCOPE grain — {@link ClinicsCubeAnalysisYearPlugin} in
 *   `clinics-cube-domain-lib`.** A per-operand `scopeCondition` on an
 *   operating-number leaf. It decides over WHICH year a metric is measured.
 *
 * They share the condition id because the backend types them as the same
 * `YearConditionRequestBody`, but they are not interchangeable and the UI must
 * not let them read as one control: this one is labelled **"Klinik-Jahr"**,
 * the leaf one **"Analysejahr"**. That distinction was the explicit UX warning
 * on the ticket, and it is not cosmetic — BOTH controls appear in the same
 * "Berechnung" side sheet (Datenbasis above, Formel below), so one shared name
 * would put two rows carrying different values and different meanings in one
 * dialog. A user reading "Analysejahr 2022" on the Datenbasis row would expect
 * 2022 figures; §1 of the ticket is explicit that a membership year does NOT
 * restrict the analysis year, so the metrics would still compute over every
 * year they have.
 *
 * The label therefore deliberately DIVERGES from the blueprint's own
 * `displayName` ("Jahr"), which is too vague to carry the distinction next to
 * Bundesland/Betten in the condition menu.
 *
 * The analysis-year plugin is form-scoped (`extraRegistrations` on the metric
 * leaf's own form) and this one is registered GLOBALLY, which is what keeps the
 * shared id unambiguous — `FilterConditionFormPluginComponent` resolves extra
 * registrations before the DI ones, so inside the leaf's form the SCOPE plugin
 * still wins.
 *
 * ## Why a bespoke plugin and not the generic blueprint LIST
 *
 * The hybrid resolver would otherwise synthesize a `GenericListPlugin` for this
 * blueprint, and all three of its limits bite here — the same three the
 * analysis-year plugin documents:
 *
 * 1. The `year` blueprint ships NO options source (no `catalogReference`, no
 *    `labeledOptions`), so the generic branch renders an EMPTY picker. Years
 *    come from `…/operating-numbers/available-years` instead, handed in via
 *    `pluginConfig` — which is also why this plugin does not fetch them itself.
 * 2. A generic list's `toText()` reports a COUNT ("1 Jahr"). A year is always
 *    worth printing, so this one prints the values.
 * 3. A generic list's values are `string[]`; the wire is
 *    `YearConditionRequestBody`, whose `includes`/`excludes` are INTEGER
 *    arrays. {@link bdParseYearList} does the coercion once, here.
 *
 * Registering this plugin also REMOVES the empty generic "Jahr" from the plain
 * Klinikfilter's add-wizard: `resolveClinicConditions` now takes its `existing`
 * branch for the blueprint, and `buildDynamicClinicFilterInputs` only offers
 * the GENERIC ones at the top level. Offering it there is a separate decision —
 * it would need an entry in `AysClinicsFilter.SPECIFICATION`.
 *
 * ## Deliberately NOT in any analysis SPECIFICATION
 *
 * Registered so a stored `year` node rehydrates anywhere (the cohort's
 * read-only view and the Kachelinfo panel both resolve against the global
 * registrations), but OFFERED only through the cube cohort's
 * `additionalPluginTypes` — the same arrangement `clinicsFilterIds` uses.
 */
export const FilterConditionClinicYearType: FilterConditionPluginSpecification<
  number[],
  FilterConditionClinicYearDto,
  BdClinicYearCondition
> = {
  type: 'year',
  category: 'clinic',
  displayName: { de: 'Klinik-Jahr' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(obj: FilterConditionClinicYearDto): FilterConditionClinicYearPlugin {
    return new FilterConditionClinicYearPlugin(
      bdParseYearList(obj.includes),
      bdParseYearList(obj.excludes),
    );
  },
  fromCondition(jsonObj: BdClinicYearCondition): FilterConditionClinicYearPlugin {
    return new FilterConditionClinicYearPlugin(
      bdParseYearList(jsonObj.includes),
      bdParseYearList(jsonObj.excludes),
    );
  },
  newInstance(): FilterConditionClinicYearPlugin {
    return new FilterConditionClinicYearPlugin([]);
  },
};

export class FilterConditionClinicYearPlugin extends FilterConditionPluginImpl<
  number[],
  FilterConditionClinicYearDto
> {
  public static override Specification = FilterConditionClinicYearType;

  /**
   * `excludes` is round-tripped but never edited here: the wire supports it and
   * a tree authored elsewhere may carry one, so dropping it on a save would
   * silently widen someone's pool. The form only ever writes `includes`.
   */
  constructor(
    private includes: number[],
    private excludes: number[] = [],
  ) {
    super(FilterConditionClinicYearType.type);
  }

  override clone(): FilterConditionClinicYearPlugin {
    return new FilterConditionClinicYearPlugin(
      [...this.includes],
      [...this.excludes],
    );
  }

  /** A new instance with the same foreign `excludes` and a fresh selection. */
  public withValue(includes: number[]): FilterConditionClinicYearPlugin {
    return new FilterConditionClinicYearPlugin([...includes], [...this.excludes]);
  }

  override equals(other: FilterConditionClinicYearPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  /**
   * Empty is INVALID here, unlike the analysis year.
   *
   * On a metric leaf an empty scope means "every year the metric has", which is
   * the contract's own default. In a clinic pool it means nothing: the node
   * narrows by year or it should not be in the tree at all, and every other
   * cohort condition demands a value the same way.
   */
  override validationToText(): string {
    return this.includes.length === 0 ? 'Mindestens ein Klinik-Jahr auswählen' : '';
  }

  override toCondition(): BdCondition {
    return new BdClinicYearCondition(this.includes, this.excludes);
  }

  override toJSON(): FilterConditionClinicYearDto {
    return {
      type: this.type,
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  /**
   * Values, newest first — never a count.
   *
   * This is the compact `filter-condition-expression-show` text, which is what
   * the cohort editor's summary row renders. "2 Jahre" there would make the
   * user open the dialog to find out WHICH, and a year is short enough that it
   * never needs to.
   */
  override toText(): string {
    return this.includes.length === 0
      ? 'Klinik-Jahr'
      : `Klinik-Jahr ${bdYearListText(this.includes)}`;
  }

  /**
   * The detailed view splits title from values, so the title stays the bare
   * condition NAME here — letting {@link toText} through would print the years
   * twice, once in the heading and once in the list below it.
   */
  override toDetailedText(): FilterDetailedText | null {
    return {
      title: 'Klinik-Jahr',
      items: [...this.includes]
        .sort((a, b) => b - a)
        .map((year) => String(year)),
    };
  }

  override toValue(): number[] {
    return this.includes;
  }
}

export class BdClinicYearCondition extends BdContentCondition<number> {
  constructor(includes: number[], excludes: number[]) {
    super(AYS_CLINIC_YEAR_CONDITION_ID, includes, excludes);
  }
}

export interface FilterConditionClinicYearDto {
  type: string;
  includes: number[];
  excludes?: number[];
}

/** Years as one string, newest first — the one spelling of a year selection. */
export function bdYearListText(years: readonly number[]): string {
  return [...years].sort((a, b) => b - a).join(', ');
}

/**
 * Coerce whatever a stored condition carries into real years.
 *
 * A year body is integers on the wire, but this repo has been bitten more than
 * once by years arriving as STRINGS out of a saved payload, so the conversion
 * is explicit and anything that is not a year is DROPPED rather than sent on.
 *
 * ⚠ Do not simplify this to `Number(value)` + `Number.isFinite`. `Number(null)`
 * is `0`, not `NaN`, and `Number('')` is `0` too — so a null in a stored list
 * became the year ZERO and passed a finite check, putting `includes: [2024, 0]`
 * on the wire. Only numbers and non-blank numeric strings are converted, and
 * the result has to be a positive integer to survive.
 *
 * Shared with `clinics-cube-domain-lib`'s Analysejahr plugin: the two spellings
 * of a year condition must coerce identically or a payload that round-trips
 * through one breaks in the other.
 */
export function bdParseYearList(
  values: readonly unknown[] | null | undefined,
): number[] {
  return (values ?? [])
    .map((value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '') return Number(value);
      return Number.NaN;
    })
    .filter((value) => Number.isInteger(value) && value > 0);
}
